import { isValidDate } from '@patternfly/react-core';

export function required(message, t) {
  const errorMessage = message || (t ? t`This field must not be blank` : 'This field must not be blank');
  return (value) => {
    if (typeof value === 'string' && !value.trim()) {
      return errorMessage;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return undefined;
    }
    if (!value) {
      return errorMessage;
    }
    return undefined;
  };
}

export function validateTime(t) {
  return (value) => {
    const timeRegex = new RegExp(
      `^\\s*(\\d\\d?):([0-5])(\\d)\\s*([AaPp][Mm])?\\s*$`
    );
    let message;
    const timeComponents = value.split(':');

    const date = new Date();
    date.setHours(parseInt(timeComponents[0], 10));
    date.setMinutes(parseInt(timeComponents[1], 10));

    if (!isValidDate(date) || !timeRegex.test(value)) {
      message = t ? t`Invalid time format` : 'Invalid time format';
    }

    return message;
  };
}

export function maxLength(max, t) {
  return (value) => {
    if (value.trim().length > max) {
      return t ? t`This field must not exceed ${max} characters` : `This field must not exceed ${max} characters`;
    }
    return undefined;
  };
}

export function minLength(min, t) {
  return (value) => {
    if (value.trim().length < min) {
      return t ? t`This field must be at least ${min} characters` : `This field must be at least ${min} characters`;
    }
    return undefined;
  };
}

export function minMaxValue(min, max, t) {
  return (value) => {
    if (!Number.isFinite(min) && value > max) {
      return t ? t`This field must be a number and have a value less than ${max}` : `This field must be a number and have a value less than ${max}`;
    }
    if (!Number.isFinite(max) && value < min) {
      return t ? t`This field must be a number and have a value greater than ${min}` : `This field must be a number and have a value greater than ${min}`;
    }
    if (value < min || value > max) {
      return t ? t`This field must be a number and have a value between ${min} and ${max}` : `This field must be a number and have a value between ${min} and ${max}`;
    }
    return undefined;
  };
}

export function requiredEmail(t) {
  return (value) => {
    if (!value) {
      return t ? t`This field must not be blank` : 'This field must not be blank';
    }

    // This isn't a perfect validator. It's likely to let a few
    // invalid (though unlikely) email addresses through.

    // This is ok, because the server will always do strict validation for us.

    const splitVals = value.split('@');

    if (splitVals.length >= 2) {
      if (splitVals[0] && splitVals[1]) {
        // We get here if the string has an '@' that is enclosed by
        // non-empty substrings
        return undefined;
      }
    }

    return t ? t`Invalid email address` : 'Invalid email address';
  };
}

export function noWhiteSpace(t) {
  return (value) => {
    if (/\s/.test(value)) {
      return t ? t`This field must not contain spaces` : 'This field must not contain spaces';
    }
    return undefined;
  };
}

export function integer(t) {
  return (value) => {
    const str = String(value);
    if (!Number.isInteger(value) && /[^0-9]/.test(str)) {
      return t ? t`This field must be an integer` : 'This field must be an integer';
    }
    return undefined;
  };
}

export function number(t) {
  return (value) => {
    const str = String(value);
    if (/^-?[0-9]*(\.[0-9]*)?$/.test(str)) {
      return undefined;
    }
    // large number scientific notation (e.g. '1e+21')
    if (/^-?[0-9]*e[+-][0-9]*$/.test(str)) {
      return undefined;
    }
    return t ? t`This field must be a number` : 'This field must be a number';
  };
}

export function twilioPhoneNumber(t, plural) {
  return (value) => {
    const phoneNumbers = Array.isArray(value) ? value : [value];
    let error;
    if (!error) {
      phoneNumbers.forEach((v) => {
        if (!/^\s*(?:\+?(\d{1,3}))?[. (]*(\d{7,12})$/.test(v)) {
          if (plural) {
            error = plural(phoneNumbers.length, {
              one: 'Please enter a valid phone number.',
              other: 'Please enter valid phone numbers.',
            });
          } else {
            error = phoneNumbers.length === 1
              ? 'Please enter a valid phone number.'
              : 'Please enter valid phone numbers.';
          }
        }
      });
    }
    return error;
  };
}

export function url(t) {
  return (value) => {
    if (!value) {
      return undefined;
    }
    // URL regex from https://urlregex.com/
    if (
      // eslint-disable-next-line max-len
      !/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www\.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w\-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[.!/\\\w]*))?)/.test(
        value
      )
    ) {
      return t ? t`Please enter a valid URL` : 'Please enter a valid URL';
    }
    return undefined;
  };
}

export function combine(validators) {
  return (value) => {
    for (let i = 0; i < validators.length; i++) {
      const validate = validators[i];
      const error = validate ? validate(value) : null;
      if (error) {
        return error;
      }
    }
    return undefined;
  };
}

export function regExp(t) {
  return (value) => {
    try {
      RegExp(value);
    } catch {
      return t ? t`This field must be a regular expression` : 'This field must be a regular expression';
    }
    return undefined;
  };
}

export function requiredPositiveInteger(t) {
  return (value) => {
    if (typeof value === 'number') {
      if (!Number.isInteger(value)) {
        return t ? t`This field must be an integer` : 'This field must be an integer';
      }
      if (value < 1) {
        return t ? t`This field must be greater than 0` : 'This field must be greater than 0';
      }
    }
    if (!value) {
      return t ? t`Select a value for this field` : 'Select a value for this field';
    }
    return undefined;
  };
}
